/**
 * @fileoverview Membership and promoter management actions
 * @module MembershipActions
 */

import apiClient from "../utils/apiClient";
import useSWR from "swr";

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns the values for promoters
 * @function
 * @name usePromoters
 * @memberof module:MembershipActions
 * @param {Object} params - Status and size parameters for the promoters
 * @returns {SWRObject} SWR Object containing promoters data
 */

export const usePromoters = (params) => {
  const fetcher = ({ url, params }) =>
    apiClient.get(url, { params }).then((res) => res?.data?.results);

  const { data, error } = useSWR({ url: "/membership", params }, fetcher);

  return { data, isLoading: !error && !data, error };
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns the values for a single promoter
 * @function
 * @name usePromoter
 * @memberof module:MembershipActions
 * @param {string|number} id - ID for the promoter
 * @returns {SWRObject} SWR Object containing promoter data
 */

export const usePromoter = (id) => {
  const fetcher = ({ url, params }) =>
    apiClient.get(url, { params }).then((res) => res?.data);
  const { data, error, mutate } = useSWR({ url: `/membership/${id}` }, fetcher);

  return { data: data, isLoading: !error && !data, error, mutate };
};
